import { onMounted, onUnmounted, ref } from "vue";
import { SupplementApi } from "../../stores/supplement-api";
import { isSaveInFlight, isLabelModalOpen, isLabelWriteInFlight } from "./use-auto-save";
import { useSafeTimeout, useEventListeners } from "../utils/timer-utils";

/**
 * Poll for external/concurrent edits that could cause a conflict.
 * Polls the /poll endpoint to check if lock_version has changed on the server.
 * Uses idle detection and page visibility to be resource-efficient.
 *
 * @param {Object} store - The supplement label store
 * @param {number|string} supplementId - The label ID
 * @param {Object} options - Polling configuration
 * @param {number} options.activeInterval - Interval when user is active
 * @param {number} options.idleInterval - Interval when user is idle
 * @param {number} options.idleTimeout - Time before considered idle
 */
export function useVersionPolling(store, supplementId, options = {}) {
  const { activeInterval = 5000, idleInterval = 60000, idleTimeout = 15000 } = options;

  const pollTimer = useSafeTimeout();
  const idleTimerUtil = useSafeTimeout();
  const isIdle = ref(false);
  const isPolling = ref(false);
  let consecutiveFailures = 0;
  const MAX_SILENT_FAILURES = 5;

  const resetIdleTimer = () => {
    isIdle.value = false;
    idleTimerUtil.set(() => {
      store.debugLog("[VersionPolling] User is idle");
      isIdle.value = true;
    }, idleTimeout);
  };

  const checkVersion = async () => {
    // Don't poll if already in conflict state, page is hidden, save is in flight,
    // or a label modal is open (version may be mid-update from logo/barcode op)
    if (
      store.isConflicted ||
      document.hidden ||
      isSaveInFlight() ||
      isLabelModalOpen() ||
      isLabelWriteInFlight()
    ) {
      store.debugLog("[VersionPolling] Skipped poll");
      return;
    }

    store.debugLog("[VersionPolling] Checking version...");

    try {
      const { response, data } = await SupplementApi.pollLabelVersion(supplementId);

      if (response.ok) {
        consecutiveFailures = 0;
        if (data?.lock_version !== undefined) {
          store.debugLog("[VersionPolling] Server lock version:", data.lock_version);
          store.setServerLockVersion(data.lock_version);
        }
      }
    } catch {
      consecutiveFailures++;
      if (consecutiveFailures >= MAX_SILENT_FAILURES) {
        store.debugLog("[VersionPolling] Multiple poll failures:", consecutiveFailures);
        consecutiveFailures = 0;
      }
    }
  };

  const scheduleNextPoll = () => {
    // Don't schedule if page is hidden
    if (document.hidden) {
      store.debugLog("[VersionPolling] Not scheduling next poll (hidden)");
      pollTimer.clear();
      return;
    }

    const interval = isIdle.value ? idleInterval : activeInterval;
    pollTimer.set(async () => {
      await checkVersion();
      scheduleNextPoll();
    }, interval);
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      // Page hidden - stop polling
      store.debugLog("[VersionPolling] Page hidden, pausing");
      pollTimer.clear();
    } else {
      // Page visible - check immediately and resume polling
      store.debugLog("[VersionPolling] Page visible, resuming");
      checkVersion();
      scheduleNextPoll();
    }
  };

  const handleUserActivity = () => {
    resetIdleTimer();
  };

  // Event listener management for user activity detection
  const activityListeners = useEventListeners(
    document,
    ["mousemove", "keydown", "click", "scroll"],
    handleUserActivity
  );

  const start = () => {
    if (isPolling.value) return;
    store.debugLog("[VersionPolling] Starting polling");
    isPolling.value = true;

    // Set up idle detection
    resetIdleTimer();
    activityListeners.add();

    // Set up visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Start polling
    scheduleNextPoll();
  };

  const stop = () => {
    store.debugLog("[VersionPolling] Stopping polling");
    isPolling.value = false;
    pollTimer.clear();
    idleTimerUtil.clear();
    activityListeners.remove();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };

  onMounted(() => {
    start();
  });

  onUnmounted(() => {
    stop();
  });

  return {
    isIdle,
    isPolling,
    checkVersion,
    start,
    stop,
  };
}
